import {AceLoggerService} from '@logging/ace-logger.service';
import {Component, EventEmitter, forwardRef, inject, Injector, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges, ViewChild} from '@angular/core';
import {DeviceObject, EquipmentId, Favourite} from '@app/graphql-typings/graphql';
import {MessageTranslateService} from '../../services';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {AceSelectComponent} from '../ace-select/ace-select.component';
import {finalize, takeUntil} from 'rxjs/operators';
import {AceLogger} from '@logging/ace-logger';
import {BacnetDeviceObjectCache} from "@shared/data-stores/bacnet-device-object-cache";
import {FavouritesCache, FavouritesCacheImpl} from "@shared/data-stores/favourites-cache";
import {Subject, Subscription} from "rxjs";
import {DeviceObjectId} from "@shared/util/app-types";
import {ArrayUtils, IterableUtil} from "@shared/util/collections/collection-utils";
import {deferTask} from "@shared/util/angular/angular-utils";
import {objectsEqual} from "@shared/util/comparitors/objects-comparator";


export interface FavouritesSelectSearchTerm {
  unitIds?: number[];
}


@Component({
  selector: 'app-favourites-select',
  templateUrl: './favourites-select.component.html',
  styleUrls: ['./favourites-select.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => FavouritesSelectComponent)},
    {
      // Use an ancestor's cache when there is one, so a page fetches a favourite once. Otherwise make our own cache.
      provide: FavouritesCache,
      useFactory: () => inject(FavouritesCache, {optional: true, skipSelf: true})
        ?? Injector.create({providers: [{provide: FavouritesCache, useClass: FavouritesCacheImpl}], parent: inject(Injector)})
          .get(FavouritesCache)
    }
  ],
  standalone: false
})
export class FavouritesSelectComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {

  private readonly ngUnsubscribe = new Subject<void>();

  @Input() equipmentId: EquipmentId;
  @Input() includeIdentifierInTooltip = false;
  @Input() multiSelect = true;
  @Input() bindLabel: string;
  @Input() bindValue: string;
  @Input() placeholder: string;
  @Input() hideSelected = true;
  @Input() favourites: Favourite[] = [];
  @Input() optionsSearchTerm: FavouritesSelectSearchTerm | null = null;
  @Input() appendTo: string;
  @Input() isReadOnly = false;
  @Input() loading = false;
  @Output() loadingChange = new EventEmitter<boolean>();
  @Output() favouritesLoaded = new EventEmitter<Favourite[]>();

  @ViewChild('aceSelect', {static: true})
  private aceSelect: AceSelectComponent;

  private readonly logger: AceLogger;
  private favouritesFetchSubscription: Subscription;

  readonly MAX_ITEMS = 3;
  readonly TOOLTIP_OPEN_DELAY_MS = 1000;
  selectedFavourites: Favourite[] | Favourite;
  favouriteOptions: Favourite[] = [];
  deviceObjectFetchSubscription: Subscription;
  deviceObjectsById: Map<DeviceObjectId, DeviceObject>;

  constructor(private translateService: MessageTranslateService,
              private favouritesCache: FavouritesCache,
              @Optional() private bacnetDeviceObjectCache: BacnetDeviceObjectCache,
              logService: AceLoggerService) {
    this.logger = logService.create(FavouritesSelectComponent.name);
  }

  onChange: (_) => void = (_: any): void => {};
  onTouched: () => void = (): void => {};

  aceSelectChange = (selected: any) => {
    this.selectedFavourites = selected;
    this.logger.debug('aceSelectChange() selectedFavourites', this.selectedFavourites);
    this.onChange(selected);
  };

  aceSelectTouched = () => {
    this.logger.debug('aceSelectTouched()');
    this.onTouched();
  };

  ngOnInit(): void {
    if (!this.placeholder) {
      this.placeholder = this.translateService.instant('Favourites');
    }

    this.aceSelect.registerOnChange(this.aceSelectChange);
    this.aceSelect.registerOnTouched(this.aceSelectTouched);
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  writeValue(selected: any): void {
    this.selectedFavourites = selected;
    this.logger.debug('writeValue() selectedFavourites', this.selectedFavourites);
    this.aceSelect.writeValue(selected);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Fetch when equipmentId changes with null favourites (parent sets to null on user equipment change)
    if (changes.equipmentId && (!changes.favourites || this.favourites == null)) {
      this.loadFavouritesByEquipmentId(true);
    }
    const optionsSearchTermChanged = changes.optionsSearchTerm
      && !this.searchTermsEqual(changes.optionsSearchTerm.previousValue, changes.optionsSearchTerm.currentValue);
    if (changes.favourites || optionsSearchTermChanged) {
      this.updateFavouriteOptions();
    }
    if (changes.includeIdentifierInTooltip) {
      if (this.includeIdentifierInTooltip && !this.bacnetDeviceObjectCache) {
        throw new Error('When includeIdentifierInTooltip is true, bacnetDeviceObjectCache needs to be provided.');
      }
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.aceSelect.setDisabledState(isDisabled);
  }

  private loadFavouritesByEquipmentId(clearSelectedIfNeeded: boolean): void {
    this.favouritesFetchSubscription?.unsubscribe();
    if (this.equipmentId != null) {
      /** When cache is warm, subscribe runs synchronously changing a value already checked in the parent component. This prevents that
       * by not emitting `loadingChange` when favourites response is synchronous. */
      let isImmediateResponse = true;
      this.favouritesFetchSubscription = this.favouritesCache.getFavouritesForEquipment(this.equipmentId)
        .pipe(
          finalize(() => {
            if (!isImmediateResponse) {
              this.loading = false;
              this.loadingChange.emit(this.loading);
            }
          }),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe((favourites: Favourite[]) => {
          this.setFavourites(favourites);
          if (isImmediateResponse) {
            deferTask(() => this.favouritesLoaded.emit(favourites));
          } else {
            this.favouritesLoaded.emit(favourites);
          }
        });
      if (!this.favouritesFetchSubscription.closed) {
        isImmediateResponse = false;
        this.loading = true;
        this.loadingChange.emit(this.loading);
      }
    } else {
      this.setFavourites([]);
      if (clearSelectedIfNeeded) {
        const emptyValue: Favourite[] | null = this.multiSelect ? [] : null;
        this.aceSelect.writeValue(emptyValue);
        this.aceSelectChange(emptyValue);
      }
    }
  }

  private setFavourites(favourites: Favourite[]): void {
    this.favourites = favourites;
    this.updateFavouriteOptions();
  }

  private updateFavouriteOptions(): void {
    const favourites = this.favourites ?? [];
    const matchesSearchTerm = this.buildFavouritePredicate(this.optionsSearchTerm);
    this.favouriteOptions = matchesSearchTerm ? favourites.filter(matchesSearchTerm) : favourites;
    this.adjustSelectedFavourites(matchesSearchTerm);
  }

  /** Null when the search term restricts nothing, so that the options are kept as they are given */
  private buildFavouritePredicate(searchTerm: FavouritesSelectSearchTerm | null): ((favourite: Favourite) => boolean) | null {
    const predicates: ((favourite: Favourite) => boolean)[] = [];
    if (searchTerm?.unitIds?.length) {
      const unitIds = new Set<number>(searchTerm.unitIds);
      predicates.push(favourite => {
        const unitId = FavouritesSelectComponent.getUnitId(favourite);
        return unitId != null && unitIds.has(unitId);
      });
    }
    return predicates.length ? favourite => predicates.every(predicate => predicate(favourite)) : null;
  }

  /** Prune selected favourites not in options anymore */
  private adjustSelectedFavourites(matchesSearchTerm: ((favourite: Favourite) => boolean) | null): void {
    if (matchesSearchTerm == null || this.favourites == null || this.selectedFavourites == null) {
      return;
    }
    const selectedFavourites = ArrayUtils.asArray(this.selectedFavourites);
    const keptFavourites = selectedFavourites.filter(matchesSearchTerm);
    if (keptFavourites.length !== selectedFavourites.length) {
      const value = this.multiSelect ? keptFavourites : keptFavourites[0] ?? null;
      deferTask(() => {
        this.writeValue(value);
        this.onChange(value);
      });
    }
  }

  private searchTermsEqual(term1: FavouritesSelectSearchTerm | null, term2: FavouritesSelectSearchTerm | null): boolean {
    return objectsEqual(term1 ?? null, term2 ?? null);
  }

  compareFn(a: Favourite, b: Favourite): boolean {
    return a.fav_id === b.fav_id;
  }

  getDisplayName(favourite: Favourite): string {
    return favourite.metadata.name + (favourite.zone.system ? '' : ' - ' + favourite.zone.level.level_name + ', ' + favourite.zone.zone_name.zone_name);
  }

  hasBacnetObject(favourite: Favourite): boolean {
    return !!favourite.device_object_id;
  }

  loadContentForTooltip() {
    if (this.includeIdentifierInTooltip) {
      const selectedFavourites: Favourite[] = ArrayUtils.asArray(this.selectedFavourites);
      if (selectedFavourites.length) {
        const deviceObjectIds = selectedFavourites.reduce((acc, fav) => {
          if (fav.device_object_id != null) {
            acc.add(fav.device_object_id);
          }
          return acc;
        }, new Set<DeviceObjectId>());
        if (IterableUtil.some(deviceObjectIds, favId => !this.deviceObjectsById?.has(favId))) {
          this.deviceObjectFetchSubscription?.unsubscribe();
          this.deviceObjectFetchSubscription = this.bacnetDeviceObjectCache.getDeviceObjectsByIds(Array.from(deviceObjectIds)).pipe(
            finalize(() => this.deviceObjectFetchSubscription = null),
            takeUntil(this.ngUnsubscribe)
          ).subscribe(deviceObjectsByFavId => this.deviceObjectsById = deviceObjectsByFavId);
        }
      }
    }
  }

  static getUnitId(favourite: Favourite | null): number | null {
    return favourite?.metadata?.unit?.unit_id ?? null;
  }

  /** The unit id shared by all the favourites, or null when they do not all share one */
  static getSharedUnitId(favourites: Favourite[]): number | null {
    const unitId = FavouritesSelectComponent.getUnitId(favourites[0] ?? null);
    return favourites.every(favourite => FavouritesSelectComponent.getUnitId(favourite) === unitId) ? unitId : null;
  }
}
